import { Form, Row, Col, Button } from 'react-bootstrap';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextInput, TextAreaInput, BrandSelectInput } from '../Misc/FormInput';
import { VendorSelection } from './ProductFormSimpleSections';
import { authFetch } from '../Auth/apiClient';
import { useMsal } from '@azure/msal-react';
import { getUserInfo } from '../Auth/fetchUser';
import { deleteProduct } from './productActions';
import 'bootstrap/dist/css/bootstrap.min.css';
import './ProductForm.css';

const INITIAL_FORM_VALUES = {
    sku: '',
    manufacturer_num: '',
    in_stock_sku_list: '',
    display_sku: '',
    name: '',
    brand_id: 0,
    brand_id_2: 0,
    brand_id_3: 0,
    brand_id_4: 0,
    vendor_id: '',
    sale_header: '',
    save: '',
    regular_price: '',
    sale_price: '',
    regular_price_map: '',
    sale_price_map: '',
    image_name: '',
    bullet1: '',
    bullet2: '',
    bullet3: '',
    bullet4: '',
    bullet5: '',
    disclaimer: '',
    comments: '',
    coop_vendor_code: '',
    coop_eligible: 'NO Co-op program',
    coop_comments: '',
    hint: '',
    hires_image_name: '',
    hires_image_name_2: '',
    hires_image_name_3: '',
    product_id: '',
    coop_pre_approval: false,
    buyer_responsible: '',
};

const INITIAL_IMAGE_VALUES = {
    currentlyLive: '',
    currentlyLiveCheck: false,
    sentToVendor: '',
    sentToVendorCheck: false,
    comments: '',
    commentsCheck: false,
};

const COOP_OPTIONS = [
    {
        label: 'NO Co-op Program',
        value: 'NO Co-op program',
        values: ['NO Co-op program', 'NO Co-op program-Track Expense'],
    },
    {
        label: 'YES Co-op, Send Invoice',
        value: 'YES Co-op, Send Invoice',
        values: ['YES Co-op by Invoice', 'YES Co-op, Send Invoice'],
    },
    {
        label: 'YES Co-op, Do Not Send Invoice',
        value: 'YES Co-op, Do Not Send Invoice',
        values: ['yesCoopDFI', 'YES Co-op DFI %', 'YES Co-op installment-Track Expenses', 'YES Co-op, Do Not Send Invoice'],
    },
];

const ProductForm = () => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    
    const [brands, setBrands] = useState([]);
    useEffect(() => {
        authFetch(instance, '/api/fetch_brands')
            .then((res) => res.json())
            .then((data) => setBrands(data))
            .catch(() => {
                alert('There was an error connecting to the database, please refresh the page.');
            });
    }, [instance]);

    const [vens, setVens] = useState({});
    useEffect(() => {
        authFetch(instance, '/api/fetch_full_vens')
            .then((res) => res.json())
            .then((data) => setVens(data))
            .catch(() => {
                alert('There was an error connecting to the database, please refresh the page.');
            });
    }, [instance]);

    const locationState = useLocation().state || {};
    const normalizeCoopEligible = (value) => {
        switch (value) {
            case 'Y':
                return 'YES Co-Op by Invoice';
            case 'N':
                return 'NO Co-op program';
            default:
                return 'NO Co-op program';
        }
    };

    const [productFormState, setProductFormState] = useState(() => ({
        ...INITIAL_FORM_VALUES,
        ...Object.keys(locationState).reduce((acc, key) => {
            if (locationState[key] !== undefined && locationState[key] !== null) {
                acc[key] = locationState[key];
            }
            return acc;
        }, {}),
        brand_id: Number(locationState.brand_id) || 0,
        brand_id_2: Number(locationState.brand_id_2) || 0,
        brand_id_3: Number(locationState.brand_id_3) || 0,
        brand_id_4: Number(locationState.brand_id_4) || 0,
        coop_eligible: normalizeCoopEligible(locationState.coop_eligible),
        coop_pre_approval: Boolean(locationState.coop_pre_approval),
    }));

    const [newImageState, setNewImageState] = useState(INITIAL_IMAGE_VALUES);

    const [userName, setUserName] = useState('');
    useEffect(() => {
        const userInfo = getUserInfo(instance);
        const name = userInfo?.name || '';
        setUserName(name);
        setProductFormState((prev) => {
            if (prev.buyer_responsible === '') {
                return { ...prev, buyer_responsible: name };
            }
            return prev;
        });
    }, [instance]);

    const handleProductFormChange = (event) => {
        const { target: { name, value } } = event;
        setProductFormState((prev) => {
            if (name.startsWith('brand_id')) {
                return { ...prev, [name]: parseInt(value) || 0 };
            }
            if (name === 'coop_pre_approval') {
                return { ...prev, [name]: !prev.coop_pre_approval };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleNewImageFormChange = (event) => {
        const { target: { name, value } } = event;
        setNewImageState((prev) => ({ ...prev, [name]: value }));
    };

    const validateNewImageStatus = () => {
        const hasCurrentlyLive = newImageState.currentlyLive !== '' || newImageState.currentlyLiveCheck;
        const hasSentToVendor = newImageState.sentToVendor !== '' || newImageState.sentToVendorCheck;
        const hasComments = newImageState.comments !== '' || newImageState.commentsCheck;
        return hasCurrentlyLive || hasSentToVendor || hasComments;
    };

    const appendImageNotesToComments = () => {
        let notes = '';
        if (newImageState.currentlyLive !== '') {
            notes += `\nImage URL: ${newImageState.currentlyLive}`;
        }
        if (newImageState.sentToVendor !== '') {
            notes += `\nPPL sent to vendor, date: ${newImageState.sentToVendor}`;
        }
        if (newImageState.comments !== '') {
            notes += `\nImage Comments: ${newImageState.comments}`;
        }
        return notes;
    };

    const buildSubmitData = (submitAsNew = false) => {
        const data = { ...productFormState, userName };
        if (submitAsNew) {
            data.product_id = '';
        }
        if (productFormState.product_id === '' && newImageState.commentsCheck) {
            data.comments += appendImageNotesToComments();
        }
        return data;
    };

    const handleProductFormSubmit = (event) => {
        event.preventDefault();

        const submitterValue = event.nativeEvent?.submitter?.value;
        const resolvedMode = submitterValue || submitModeRef.current;
        const submitAsNew = resolvedMode === 'save-as-new' ? true : resolvedMode === 'update-existing' ? false : saveAsNew;
        
        if (productFormState.product_id === '') {
            if (!validateNewImageStatus()) {
                alert('Please fill out and check one of the PRODUCT IMAGE STATUS options.');
                return;
            }
        }

        const submitData = buildSubmitData(submitAsNew);
        authFetch(instance, '/api/upsert_product', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(submitData),
        })
            .then(() => {
                alert('Product saved, returning to product search.');
                navigate('/products');
            })
            .catch(() => {
                alert('There was an error connecting to the database, please refresh the page.');
            });
    };

    const delete_product = () => {
        deleteProduct(instance, productFormState.product_id)
            .then(() => {
                navigate('/products');
            })
            .catch(() => {
                alert('There was an error connecting to the database, please refresh the page.');
            });
    };

    const [saveAsNew, setSaveAsNew] = useState(false);
    const [skuList, setSkuList] = useState('');
    const [autoFillFormState, setAutoFillFormState] = useState({});
    const [autoFillLoading, setAutoFillLoading] = useState(false);
    const submitModeRef = useRef('update-existing');
    const previousVendorIdsRef = useRef('');
    const previousCoopIdsRef = useRef('');
    const venSelect = Object.values(vens);

    // Parse vendor IDs into array, normalize, and check if any have coop='yes'
    const parseVendorIds = (vendorString) => {
        return vendorString
            .split(',')
            .map((id) => id.trim().toLowerCase())
            .filter((id) => id.length > 0);
    };

    const hasCoopEnabledVendor = useMemo(() => {
        const vendorIds = parseVendorIds(productFormState.vendor_id);
        return vendorIds.some((vid) => vens[vid]?.coop?.toLowerCase() === 'yes');
    }, [productFormState.vendor_id, vens]);

    const hasLegacyCoopDivergence = useMemo(() => {
        const vendorIds = parseVendorIds(productFormState.vendor_id);
        const coopIds = parseVendorIds(productFormState.coop_vendor_code);
        const previousVendorIds = parseVendorIds(previousVendorIdsRef.current);
        if (coopIds.length === 0) {
            return false;
        }
        if (JSON.stringify(coopIds) === JSON.stringify(previousVendorIds)) {
            return false;
        }
        return JSON.stringify(vendorIds) !== JSON.stringify(coopIds);
    }, [productFormState.vendor_id, productFormState.coop_vendor_code]);

    const normalizedVendorIds = useMemo(
        () => parseVendorIds(productFormState.vendor_id).join(', '),
        [productFormState.vendor_id]
    );

    const normalizedCoopVendorIds = useMemo(
        () => parseVendorIds(productFormState.coop_vendor_code).join(', '),
        [productFormState.coop_vendor_code]
    );

    const shouldShowCoopSection = useMemo(() => {
        return hasCoopEnabledVendor || hasLegacyCoopDivergence;
    }, [hasCoopEnabledVendor, hasLegacyCoopDivergence]);

    const shouldShowCoopVendorAboveVendor = useMemo(() => {
        return shouldShowCoopSection && normalizedVendorIds === '' && normalizedCoopVendorIds !== '';
    }, [normalizedCoopVendorIds, normalizedVendorIds, shouldShowCoopSection]);

    // Auto-sync coop_vendor_code to vendor_id when vendor_id changes (unless legacy divergence exists)
    useEffect(() => {
        const currentVendorIds = productFormState.vendor_id;
        const currentCoopIds = productFormState.coop_vendor_code;
        const prevVendorIds = previousVendorIdsRef.current;
        const prevCoopIds = previousCoopIdsRef.current;
        
        // Detect if vendor_id changed
        if (currentVendorIds !== prevVendorIds) {
            const coopIdsParsed = parseVendorIds(currentCoopIds);
            const prevIdsParsed = parseVendorIds(prevVendorIds);
            
            // Only auto-sync if coop is empty or matches the previous vendor IDs (user hasn't diverged)
            if (coopIdsParsed.length === 0 || JSON.stringify(coopIdsParsed) === JSON.stringify(prevIdsParsed)) {
                setProductFormState((prev) => ({
                    ...prev,
                    coop_vendor_code: currentVendorIds,
                }));
                previousCoopIdsRef.current = currentVendorIds;
            }
            
            previousVendorIdsRef.current = currentVendorIds;
        } else if (currentCoopIds !== prevCoopIds) {
            // Track coop changes to detect user divergence from vendor list
            previousCoopIdsRef.current = currentCoopIds;
        }
    }, [productFormState.vendor_id, productFormState.coop_vendor_code]);

    const dataFromAutoFill = (key) => {
        setProductFormState((prev) => ({ ...prev, [key]: autoFillFormState[key] }));
        setAutoFillFormState((prev) => {
            const updated = { ...prev };
            delete updated[key];
            if (`${key}_name` in updated) {
                delete updated[`${key}_name`];
            }
            return updated;
        });
    };

    const queryAutoFill = () => {
        setAutoFillLoading(true);
        authFetch(instance, '/api/autofill_product', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ skus: skuList }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.status) {
                    alert(data.status === 404 ? 'No product data found.' : 'Bad request - SKU list should be composed of SKUs separated by commas.');
                    setAutoFillLoading(false);
                    return;
                }
                const tempAutofillState = {};
                const updates = {};
                Object.keys(data).forEach((key) => {
                    if (key.includes('brand')) {
                        const found_brand = brands.find((brand) => brand.name.toLowerCase() === data[key].toLowerCase());
                        if (found_brand) {
                            updates[key] = found_brand.id;
                            tempAutofillState[`${key}_name`] = found_brand.name;
                        }
                    }
                    if (key === 'buyer_responsible') {
                        tempAutofillState[key] = userName;
                    } else if (key !== 'name') {
                        tempAutofillState[key] = data[key];
                    }
                    updates[key] = data[key];
                });
                if (productFormState.product_id === '') {
                    setProductFormState((prev) => ({ ...prev, ...updates }));
                } else {
                    setAutoFillFormState(tempAutofillState);
                }
                setAutoFillLoading(false);
            })
            .catch(() => {
                alert('There was an error connecting to the database.');
                setAutoFillLoading(false);
            });
    };

    const convertVens = useCallback(() => {
        // Infer co-op eligibility from first vendor if coop_eligible not already set
        const vendorIds = parseVendorIds(productFormState.vendor_id);
        if (vendorIds.length > 0 && !productFormState.coop_vendor_code) {
            const firstVendor = vens[vendorIds[0]];
            if (firstVendor?.coop) {
                let coopValue = 'NO Co-op program';
                if (firstVendor.coop.toLowerCase() === 'yes') {
                    coopValue = firstVendor.policy?.toLowerCase().includes('invoice')
                        ? 'YES Co-op, Send Invoice'
                        : 'YES Co-op, Do Not Send Invoice';
                }
                setProductFormState((prev) => ({ ...prev, coop_eligible: coopValue }));
            }
        }
    }, [productFormState.vendor_id, productFormState.coop_vendor_code, vens]);

    useEffect(() => {
        convertVens();
    }, [convertVens]);

    const coopVendorIsRequired = !["NO Co-op program", "NO Co-op program-Track Expense", ''].includes(productFormState.coop_eligible);

    const coopEligibilitySection = shouldShowCoopSection ? (
        <div>
            <Col xs={8} className="d-flex mb-2">
                <h5 className="text-start">CO-OP ELIGIBILITY:<span style={{color: "red", fontWeight: "bold", fontSize: 18}}>*</span></h5>
            </Col>
            <VendorSelection
                formState={productFormState}
                setFormState={setProductFormState}
                fieldName="coop_vendor_code"
                inputLabel="Co-op Vendor ID:"
                asterisk={coopVendorIsRequired}
                placeholder={coopVendorIsRequired ? "(Required)" : ""}
                vendors={venSelect}
            />

            <Row>
                <Col xs={2} className="mb-3 align-items-center text-start">
                    <Form.Label className='formInput' style={{'': '20'}}>
                        Pre-approval Required
                    </Form.Label>
                    &nbsp;&nbsp;&nbsp;
                    <input
                        type="checkbox" className="pre_approval_checkbox"
                        name="coop_pre_approval"
                        onChange={handleProductFormChange}
                        checked={productFormState.coop_pre_approval}>
                    </input>
                </Col>
            </Row>

            <Form.Group as={Row} className="mb-3 text-start ms-1">
            {COOP_OPTIONS.map((option, index) => (
                <Col lg={7} key={index}>
                    <Form.Check
                        name="coop_eligible"
                        inline
                        type="radio"
                        onChange={handleProductFormChange}
                        value={option.value}
                        label={option.label}
                        checked={option.values.includes(productFormState.coop_eligible)}
                    />
                </Col>
            ))}
            </Form.Group>

            <TextAreaInput inputName="coop_comments" inputLabel="Co-op Comments:" inputValue={productFormState.coop_comments} changeFunc={handleProductFormChange}/>
        </div>
    ) : null;

    return (
        <Row>
        
        {productFormState.product_id && (
            <Col xs={12} className="text-end mb-3">
                <button onClick={() => delete_product()}>Delete Product</button>
                <hr />
            </Col>
        )}

        <div className='mb-3'>
            <Row  className="mb-3 ">
                <Col>
                    <Form.Label>Autofill (input list of skus separated by commas)</Form.Label>
                </Col>
                <Col>
                    <Form.Control
                        value={skuList}
                        onChange={(e) => setSkuList(e.target.value)}    
                    ></Form.Control>
                </Col>
                <Col>
                    <Button
                        style={{backgroundColor: "#a73c21", color: "#ffffff", textDecoration: "underline #a73c21", borderRadius: "0px",
                        borderColor: "#a73c21"}}
                        onClick={() => queryAutoFill()}
                    >Search and Autofill</Button>
                </Col>
                <Row style={{display: autoFillLoading ? '' : 'none'}}>
                    <Col className='offset-4 mt-2 text-start'>
                        <p>Loading results...</p>
                    </Col>
                </Row>
                <Col xs={12}>
                    <hr/>
                </Col>
            </Row>
        </div>
        
        <Col xs={10} className='offset-1 border-start border-end'>
        <Form onSubmit={handleProductFormSubmit}>
            {/* REQUIRED PRODUCT IDENTIFICATION INPUTS */}
            <TextInput inputName="sku" inputLabel="SKU/Item #:" asterisk={true}
            placeholder={"(Required)"}
            inputValue={productFormState.sku} inputRequired={true} changeFunc={handleProductFormChange}
            autoFillInfo={('sku' in autoFillFormState)
                ? {'value': autoFillFormState.sku,
                    'fill': () => dataFromAutoFill('sku')
                }
                : null
            }/>
            <Col className="text-start">
                <p>Manufacturer part # used by designers to find images, list PNs for items you want have pictured.</p>
            </Col>
            <TextInput inputName="manufacturer_num" inputLabel="Manufacturer part #:" asterisk={true}
            placeholder={"(Required)"}
            inputValue={productFormState.manufacturer_num} inputRequired={false} changeFunc={handleProductFormChange}
            autoFillInfo={('manufacturer_num' in autoFillFormState)
                ? {'value': autoFillFormState.manufacturer_num,
                    'fill': () => dataFromAutoFill('manufacturer_num')
                }
                : null}
            />
            {/* IMAGE FIELDS FOR NEW PRODUCTS */}
            {!productFormState.product_id && (
            <Row className="product-image-status-section mb-3">
                <Col xs={8} className="d-flex">
                    <h5 className="text-start">PRODUCT IMAGE STATUS</h5>
                    <p>&nbsp;&nbsp;&nbsp;(MUST SELECT ONE):<span style={{color: "red", fontWeight: "bold", fontSize: 18}}>*</span></p>
                </Col>
                <Row>
                    <Col xs={8}>
                        {/*<Form.Check readOnly checked={newImageState.currentlyLive !== ""} className="formInput" type="checkbox" label="Product is currently live on murdochs.com:" style={{textAlign: "start", textDecoration: "none"}}/>*/}
                        <Form.Check name="currentlyLiveCheck" checked={newImageState.currentlyLiveCheck} className="formInput" type="checkbox" label="Product is currently live on murdochs.com:" style={{textAlign: "start", textDecoration: "none"}} onChange={() => setNewImageState({...newImageState, 'currentlyLiveCheck': !newImageState.currentlyLiveCheck})}/>
                    </Col>
                    <TextInput inputName="currentlyLive" inputLabel="URL:" inputValue={newImageState.currentlyLive} inputRequired={false} changeFunc={handleNewImageFormChange}/>
                </Row>
                <Row>
                    <Col xs={8}>
                        {/*<Form.Check readOnly checked={newImageState.sentToVendor !== ""} className="formInput" type="checkbox" label="PPL has been sent to vendor" style={{textAlign: "start", textDecoration: "none"}}/>*/}
                        <Form.Check checked={newImageState.sentToVendorCheck} className="formInput" type="checkbox" label="PPL has been sent to vendor" style={{textAlign: "start", textDecoration: "none"}} onChange={() => setNewImageState({...newImageState, 'sentToVendorCheck': !newImageState.sentToVendorCheck})}/>
                    </Col>
                    <TextInput inputName="sentToVendor" inputLabel="Date Sent:" inputValue={newImageState.sentToVendor} inputRequired={false} changeFunc={handleNewImageFormChange}/>
                </Row>
                <Row>
                    <Col xs={8}>
                        {/*<Form.Check readOnly checked={newImageState.comments !== ""} className="formInput" type="checkbox" label="Notes for designer on product image status:" style={{textAlign: "start", textDecoration: "none"}}/>*/}
                        <Form.Check checked={newImageState.commentsCheck} className="formInput" type="checkbox" label="Notes for designer on product image status:" style={{textAlign: "start", textDecoration: "none"}} onChange={() => setNewImageState({...newImageState, 'commentsCheck': !newImageState.commentsCheck})}/>
                    </Col>
                    <TextAreaInput inputName="comments" inputLabel="Comments:" inputValue={newImageState.comments} changeFunc={handleNewImageFormChange}/>
                </Row>
                <br />
            </Row>
            )}
            

            {/* CONTINUED REQUIRED FIELDS */}
            <TextInput inputName="in_stock_sku_list" inputLabel="In-Stock - Dynamics Item IDs"
            asterisk={true}
            placeholder={"(Required)"}
            inputValue={productFormState.in_stock_sku_list} inputRequired={true} changeFunc={handleProductFormChange}
            autoFillInfo={('in_stock_sku_list' in autoFillFormState)
                ? {'value': autoFillFormState.in_stock_sku_list,
                    'fill': () => dataFromAutoFill('in_stock_sku_list')
                }
                : null}
            />
            <TextInput inputName="display_sku" inputLabel="Printed SKU/Item #:"
            asterisk={true}
            placeholder={"(Required)"}
            inputValue={productFormState.display_sku} inputRequired={true} changeFunc={handleProductFormChange}
            autoFillInfo={('display_sku' in autoFillFormState)
                ? {'value': autoFillFormState.display_sku,
                    'fill': () => dataFromAutoFill('display_sku')
                }
                : null}
            />
            <TextInput inputName="name" inputLabel="Printed Name:"
            asterisk={true}
            placeholder={"(Required)"}
            inputValue={productFormState.name} inputRequired={true} changeFunc={handleProductFormChange}/>

            {/* SYMBOL COPYING ROW */}
            <Row className="mb-3 align-items-center text-start">
            <Form.Label column xs={3} className="formInput" style={{paddingLeft: "2%", fontSize: "20px"}}>Click to Copy:</Form.Label>
                <Col xs={1} className="d-grid">
                    <button type="button" onClick={() => {navigator.clipboard.writeText('®')}}>®</button>
                </Col>
                <Col xs={1} className="d-grid">
                    <button type="button" onClick={() => {navigator.clipboard.writeText('™')}}>™</button>
                </Col>
                <Col xs={1} className="d-grid">
                    <button type="button" onClick={() => {navigator.clipboard.writeText('½')}}>½</button>
                </Col>
                <Col xs={1} className="d-grid">
                    <button type="button" onClick={() => {navigator.clipboard.writeText('¼')}}>¼</button>
                </Col>
                <Col xs={1} className="d-grid">
                    <button type="button" onClick={() => {navigator.clipboard.writeText('¾')}}>¾</button>
                </Col>
                
            </Row>

            {/* BRAND SELECT INPUTS */}
            <BrandSelectInput inputName="brand_id" inputLabel="Brand:" inputValue={productFormState.brand_id} changeFunc={handleProductFormChange} selectOptions={brands}
            autoFillInfo={('brand_id' in autoFillFormState)
            ? {'value': autoFillFormState.brand_id,
                'name': autoFillFormState.brand_id_name,
                'fill': () => dataFromAutoFill('brand_id')
            }
            : null}/>
            <BrandSelectInput inputName="brand_id_2" inputLabel="Brand 2:" inputValue={productFormState.brand_id_2} changeFunc={handleProductFormChange} selectOptions={brands}
            autoFillInfo={('brand_id_2' in autoFillFormState)
            ? {'value': autoFillFormState.brand_id,
                'name': autoFillFormState.brand_id_2_name,
                'fill': () => dataFromAutoFill('brand_id_2')
            }
            : null}/>
            <BrandSelectInput inputName="brand_id_3" inputLabel="Brand 3:" inputValue={productFormState.brand_id_3} changeFunc={handleProductFormChange} selectOptions={brands}
            autoFillInfo={('brand_id_3' in autoFillFormState)
            ? {'value': autoFillFormState.brand_id,
                'name': autoFillFormState.brand_id_3_name,
                'fill': () => dataFromAutoFill('brand_id_3')
            }
            : null}/>
            <BrandSelectInput inputName="brand_id_4" inputLabel="Brand 4:" inputValue={productFormState.brand_id_4} changeFunc={handleProductFormChange} selectOptions={brands}
            autoFillInfo={('brand_id_4' in autoFillFormState)
            ? {'value': autoFillFormState.brand_id_4,
                'name': autoFillFormState.brand_id_4_name,
                'fill': () => dataFromAutoFill('brand_id_4')
            }
            : null}/>

            {/* REQUIRED VENDOR ID INPUT */}
            <hr/>
            <Row className="mb-2 text-start">
                <Col xs={11}>
                    <p className="mb-0">
                        At least one vendor must be selected
                    </p>
                </Col>
            </Row>
            {shouldShowCoopVendorAboveVendor && coopEligibilitySection}
            <VendorSelection
                formState={productFormState}
                setFormState={setProductFormState}
                fieldName="vendor_id"
                inputLabel="Vendor ID:"
                asterisk={true}
                placeholder="(Required - select vendors from the dropdown to add to the list)"
                vendors={venSelect}
            />
            {shouldShowCoopSection && !shouldShowCoopVendorAboveVendor && coopEligibilitySection}
            <hr/>

            {/* PROMOTIONAL INPUTS */}
            <TextInput inputName="sale_header" inputLabel={`"Your Choice" Header:`} inputValue={productFormState.sale_header} changeFunc={handleProductFormChange}/>
            <TextInput inputName="save" inputLabel="Save Circle:" inputValue={productFormState.save} inputInfo='Formatting examples: "Save 15%", "Save $10"' changeFunc={handleProductFormChange}/>

            {/* PRICING INPUTS */}
            <hr/>
            <Col xs={8} className="d-flex">
                <h5 className="text-start">Flyer Pricing</h5>
            </Col>
            <TextInput inputName="sale_price" inputLabel="Sale Price:" inputValue={productFormState.sale_price} inputInfo='Formatting examples: "11.99", "SAVE 15%", "SAVE $10"' changeFunc={handleProductFormChange}/>
            <TextInput inputName="regular_price" inputLabel="Regular Price:" inputValue={productFormState.regular_price} inputInfo='Formatting examples: "19.99", "2.99-6.99", "Reg $19.99 a pair"' changeFunc={handleProductFormChange}
            autoFillInfo={('regular_price' in autoFillFormState)
                ? {'value': autoFillFormState.regular_price,
                    'fill': () => dataFromAutoFill('regular_price')
                }
                : null}
            />
            <hr/>
            

            {/* MAP Override inputs */}
            <Col xs={8} className="d-flex">
                <h5 className="text-start">MAP Override for Sale Signs</h5>
                <p>&nbsp;&nbsp;&nbsp;(Leave blank to pull pricing from Flyer Regular Price)</p>
            </Col>
            <TextInput inputName="sale_price_map" inputLabel="Sale Price:" inputValue={productFormState.sale_price_map} inputInfo='Formatting examples: "11.99", "Save 15%", "Save $10"' changeFunc={handleProductFormChange}/>
            <TextInput inputName="regular_price_map" inputLabel="Regular Price:" inputValue={productFormState.regular_price_map} inputInfo='Formatting examples: "19.99", "2.99-6.99", "Reg $19.99 a pair"' changeFunc={handleProductFormChange}
            autoFillInfo={('regular_price_map' in autoFillFormState)
                ? {'value': autoFillFormState.regular_price_map,
                    'fill': () => dataFromAutoFill('regular_price_map')
                }
                : null}
            />
            <hr/>

            {/* PRODUCT INFORMATION INPUTS */}
            <TextInput inputName="hires_image_name" inputLabel="Hi-Res Image Path:" inputValue={productFormState.hires_image_name} changeFunc={handleProductFormChange}/>
            <TextInput inputName="hires_image_name_2" inputLabel="Hi-Res Image Path 2:" inputValue={productFormState.hires_image_name_2} changeFunc={handleProductFormChange}/>
            <TextInput inputName="hires_image_name_3" inputLabel="Hi-Res Image Path 3:" inputValue={productFormState.hires_image_name_3} changeFunc={handleProductFormChange}/>
            <TextInput inputName="bullet1" inputLabel="Bullet Point 1:" inputValue={productFormState.bullet1} changeFunc={handleProductFormChange}/>
            <TextInput inputName="bullet2" inputLabel="Bullet Point 2:" inputValue={productFormState.bullet2} changeFunc={handleProductFormChange}/>
            <TextInput inputName="bullet3" inputLabel="Bullet Point 3:" inputValue={productFormState.bullet3} changeFunc={handleProductFormChange}/>
            <TextInput inputName="bullet4" inputLabel="Bullet Point 4:" inputValue={productFormState.bullet4} changeFunc={handleProductFormChange}/>
            <TextInput inputName="bullet5" inputLabel="Bullet Point 5:" inputValue={productFormState.bullet5} changeFunc={handleProductFormChange}/>
            <TextInput inputName="disclaimer" inputLabel="Disclaimer:" inputValue={productFormState.disclaimer} changeFunc={handleProductFormChange}/>
            <TextAreaInput inputName="comments" inputLabel="Comments:" inputValue={productFormState.comments} changeFunc={handleProductFormChange}/>
            <hr/>

            {/* USER INFORMATION */}
            <TextInput inputName="buyer_responsible" inputLabel="Buyer Responsible:" placeholder={"Enter your name"} inputValue={productFormState.buyer_responsible} changeFunc={handleProductFormChange}/>
            <hr/>
            {/*<Form.Group as={Row} className="mb-3 align-items-center text-start">
                <Form.Label column xs={3} className="formInput">Buyer Responsible:</Form.Label>
                <Col xs={7} className="text-start">
                    <Form.Control
                        type="text"
                        value={productFormState.buyer_responsible}
                        onChange={handleProductFormChange}
                        placeholder="Enter your name"
                    />
                </Col>
            </Form.Group>*/}


            {/* SAVE PRODUCT */}
            {productFormState.product_id && (
            <div>
            <div className="flex-row-start text-start">
                <h5>Update Product</h5>
                <p className="ms-3">Update this existing product in the database</p>
            </div>
            <TextInput inputName="hint" inputLabel="Reference Hint:"
            asterisk={true}
            placeholder={"(Required)"}
            inputRequired={false} inputValue={productFormState.hint} changeFunc={handleProductFormChange} />
            <Form.Group as={Row} className="mb-3 text-start">
                <Col xs={3} className="text-start productFormLabel">
                    <Button type="submit" value="update-existing" style={{backgroundColor: '#a73c21'}} className="w-100" onClick={() => {
                        submitModeRef.current = 'update-existing';
                        setSaveAsNew(false);
                    }}>Update Product</Button>
                </Col>
            </Form.Group>
            <hr></hr>
            </div>
            )}
            <div className="flex-row-start text-start">
                <h5>Save as New Product</h5>
                {productFormState.product_id && (
                    <p className="ms-3">Create a new product in the database (this will not change the old version of this product)</p>
                )}
            </div>
            <TextInput inputName="hint" inputLabel="Reference Hint:"
            asterisk={true}
            placeholder={"(Required)"}
            inputRequired={false} inputValue={productFormState.hint} changeFunc={handleProductFormChange} />
            <Form.Group as={Row} className="mb-3 text-start">
                <Col xs={3} className="text-start productFormLabel">
                    <Button type="submit" value="save-as-new" style={{backgroundColor: '#a73c21'}} onClick={() => {
                        submitModeRef.current = 'save-as-new';
                        setSaveAsNew(true);
                    }} className='w-100'>Save as New Product</Button>
                </Col>
            </Form.Group>
        </Form>
            </Col>
        </Row>
    );
}
 
export default ProductForm;
